import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';
import { useMemo } from 'react';

import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import Spinner from '~components/Spinner/Spinner';
import Grid from '~components/_layout/Grid/Grid';
import { getPpccParamValueByNameAndPage, getUrlParam } from '~globals/common';
import constants from '~globals/constants';
import { toCapitalized } from '~globals/toCapitalized';
import useAllianceData from '~hooks/useAllianceData';
import { useAppSelector } from '~hooks/useRedux';

import * as styles from './HeroPaidSearch.module.css';
import DefaultContent from './child-blocks/DefaultContent';
import HeroImage from './child-blocks/HeroImage';
import SpeakWithAgent from './child-blocks/SpeakWithAgent';
import CallAgentWithZip from './child-layouts/CallAgentWithZip';
import CarrierPartner from './child-layouts/CarrierPartner';
import Partners from './child-layouts/Partners';
import PerformancePartners from './child-layouts/PerformancePartners';
import SimplifiedMobile from './child-layouts/SimplifiedMobile';
import WelcomeBack from './child-layouts/WelcomeBack';
import ZipOnly from './child-layouts/ZipOnly';
import ZipWithDrugAndDoctor from './child-layouts/ZipWithDrugAndDoctor';
import ZipWithGuidedFlow from './child-layouts/ZipWithGuidedFlow';
import ZipWithPdpMapd from './child-layouts/ZipWithPdpMapd';
import ZipWithPhoneNumber from './child-layouts/ZipWithPhoneNumber';
import ZipWithPhoneNumberSimplified from './child-layouts/ZipWithPhoneNumberSimplified';
import ZipWithPhoneNumberVerticalLegacy from './child-layouts/ZipWithPhoneNumberVerticalLegacy';
import ZipWithPhoneNumberVerticalLegacyCompact from './child-layouts/ZipWithPhoneNumberVerticalLegacyCompact';
import ZipWithPhoneNumberVerticalLegacyPlain from './child-layouts/ZipWithPhoneNumberVerticalLegacyPlain';
import ZipWithStepper from './child-layouts/ZipWithStepper';
import ZipWithStepperLegacy from './child-layouts/ZipWithStepperLegacy';
import ZipWithStepperVerticalLegacy from './child-layouts/ZipWithStepperVerticalLegacy';
import ppccAllidList from './ppccAllidList'; // todo: store this externally and query at build time

type Template =
    | 'call-agent'
    | 'call-agent-with-zip'
    | 'partners'
    | 'carrier-partner'
    | 'shop'
    | 'welcome-back'
    | 'zip-only'
    | 'zip-with-drug-and-doctor'
    | 'zip-with-email'
    | 'zip-with-guided-flow'
    | 'zip-with-pdp-mapd'
    | 'zip-with-phone-number'
    | 'zip-with-phone-number-simplified'
    | 'zip-with-phone-number-vertical-legacy'
    | 'zip-with-phone-number-vertical-legacy-compact'
    | 'zip-with-phone-number-vertical-legacy-plain'
    | 'zip-with-stepper'
    | 'zip-with-stepper-legacy'
    | 'zip-with-stepper-vertical-legacy'
    | 'simplified-mobile'
    | 'eperks'
    | 'performance-partners';

type Props = {
    template?: Template;
    mobiletemplate?: Template;
    backgroundsurface?: SupportedBackgroundColor;
    classes?: string;
    heroimagemobile: string;
    heroimagetablet: string;
    heroimagedesktop: string;
    usepartnerapi?: boolean;
    evenhorizontallayoutsplit?: boolean;
    evenhorizontallayoutsplittablet?: boolean;
    zipformheading?: string;
    zipformmainctacopy?: string;
    zipformsecondaryctacopy?: string;
    // "exclusive" means unavailable to counties on the annual exclusion list for certain benefits
    exclusiveimagemobile?: string;
    exclusiveimagetablet?: string;
    exclusiveimagedesktop?: string;
    enablebenefitpromotion?: boolean;
    includetrustpilotmobile?: boolean;
};

const HeroPaidSearch = ({
    template = 'zip-only',
    mobiletemplate = 'zip-only',
    backgroundsurface = 'bg-surface-50',
    classes,
    heroimagemobile,
    heroimagetablet,
    heroimagedesktop,
    children = null,
    usepartnerapi = false,
    evenhorizontallayoutsplit = true,
    evenhorizontallayoutsplittablet = true,
    zipformheading = '',
    zipformmainctacopy = '',
    zipformsecondaryctacopy = '',
    exclusiveimagemobile,
    exclusiveimagetablet,
    exclusiveimagedesktop,
    enablebenefitpromotion,
    includetrustpilotmobile = false,
}: React.PropsWithChildren<Props>) => {
    const allianceId = useAppSelector((state) => state.global.allianceId);
    const { stateAbbr, stateName, countyName } = useAppSelector(
        (state) => state.global.geoLocation
    );
    const isPpccAllid = ppccAllidList.includes(getUrlParam('allid') || allianceId); // yes these are case-sensitive!
    const [allianceResponse, status] =
        (isPpccAllid || template === 'carrier-partner') && usepartnerapi
            ? useAllianceData(getUrlParam('allid') || allianceId)
            : [null, 'success'];
    const partnerApiConfigs = allianceResponse?.partnerAllianceInfo?.landingPageConfigs;
    const partnerDisplayName =
        allianceResponse?.partnerAllianceInfo?.allianceData?.partnerDisplayName;
    const partnerHeroImg = partnerApiConfigs
        ? (getPpccParamValueByNameAndPage({
            pageCode: 'ehim_partner',
            pageConfigs: partnerApiConfigs,
            paramName: 'hero_image_url',
        }) as string)
        : undefined;

    const isExcludedCounty =
        stateAbbr && constants.EXCLUDED_COUNTIES.includes(`${countyName}, ${stateAbbr}`);

    const mobileImage = (!isExcludedCounty && exclusiveimagemobile) || heroimagemobile;
    const tabletImage = (!isExcludedCounty && exclusiveimagetablet) || heroimagetablet;
    const desktopImage = (!isExcludedCounty && exclusiveimagedesktop) || heroimagedesktop;

    const pageData = usePageContext();
    const annualCostDataProps = pageData.annualCostData?.customProperties ?? null;
    const currentPlanYear = annualCostDataProps ? annualCostDataProps.CURRENT_YEAR?.value : '';

    const { pathname } = pageData.location;
    const pageFilter = !isExcludedCounty
        ? pathname.includes('/enroll/dvh/')
            ? '2%3Adrugs%2Cdental'
            : pathname.includes('/enroll/zeropremium/')
                ? '2:drugs%7C1:0.0&category=ZeroMonthlyPremium'
                : undefined
        : undefined;
    const employerPaths = ['-group/', '-health-insurance/'];
    const isEmployer = useMemo(
        () => employerPaths.some((path) => pathname.includes(path)),
        [pathname]
    );
    const isStatesPage = useMemo(
        () => pathname.includes('-health-insurance/'),
        [pathname]
    );
    const zipInputLabel = useMemo(
        () => (isStatesPage ? 'ZIP code' : 'Main office ZIP code'),
        [isStatesPage]
    );
    const zipButtonTitle = useMemo(
        () => (isStatesPage ? 'Compare plans' : 'See plans'),
        [isStatesPage]
    );
    const isPerformancePartnersTemplate = ['performance-partners'].some(
        (testTemplate) => template === testTemplate
    );
    const isPaidSearchTemplate = !isPerformancePartnersTemplate;

    const templateName = useMemo(() => toCapitalized(template.split('-').join(' ')), [template]);
    const mobileTemplateName = useMemo(
        () => toCapitalized(mobiletemplate.split('-').join(' ')),
        [mobiletemplate]
    );
    const isLegacyStylingTemplate = [
        'zip-only',
        'zip-with-email',
        'zip-with-stepper-legacy',
        'zip-with-stepper-vertical-legacy',
        'zip-with-phone-number',
        'zip-with-phone-number-simplified',
        'zip-with-phone-number-vertical-legacy',
        'zip-with-phone-number-vertical-legacy-compact',
        'zip-with-phone-number-vertical-legacy-plain',
        'welcome-back',
        'zip-with-pdp-mapd',
        'call-agent',
        'call-agent-with-zip',
        'eperks',
    ].some((testTemplate) => template === testTemplate);

    const isLegacyStylingTemplateMobile = [
        'zip-only',
        'zip-with-email',
        'zip-with-stepper-legacy',
        'zip-with-stepper-vertical-legacy',
        'zip-with-phone-number',
        'zip-with-phone-number-simplified',
        'zip-with-phone-number-vertical-legacy',
        'zip-with-phone-number-vertical-legacy-compact',
        'zip-with-phone-number-vertical-legacy-plain',
        'welcome-back',
        'zip-with-pdp-mapd',
        'call-agent',
        'call-agent-with-zip',
        'simplified-mobile',
        'eperks',
    ].some((testTemplate) => mobiletemplate === testTemplate);

    const isVerticalLayoutTemplate = [
        'zip-only',
        'zip-with-email',
        'zip-with-stepper-vertical-legacy',
        'zip-with-phone-number-vertical-legacy',
        'zip-with-phone-number-vertical-legacy-compact',
        'zip-with-phone-number-vertical-legacy-plain',
        'welcome-back',
        'zip-with-pdp-mapd',
        'call-agent',
        'call-agent-with-zip',
        'eperks',
    ].some((testTemplate) => template === testTemplate);

    const isVerticalLayoutTemplateMobile = [
        'zip-only',
        'zip-with-email',
        'zip-with-stepper-vertical-legacy',
        'zip-with-phone-number-vertical-legacy',
        'zip-with-phone-number-vertical-legacy-compact',
        'zip-with-phone-number-vertical-legacy-plain',
        'welcome-back',
        'zip-with-pdp-mapd',
        'call-agent',
        'call-agent-with-zip',
        'eperks',
    ].some((testTemplate) => mobiletemplate === testTemplate);

    // determines whether selected template is `compact` in desktop view
    const isCompactTemplateInDesktopView = ['zip-with-phone-number-vertical-legacy-compact'].some(
        (testTemplate) => template === testTemplate
    );

    // determines whether selected template is `compact` in tablet view
    const isCompactTemplateInTabletView = [
        'zip-only',
        'zip-with-email',
        'zip-with-phone-number-vertical-legacy-compact',
        'call-agent',
    ].some((testTemplate) => mobiletemplate === testTemplate);

    // removes the extra left padding from the hero image
    const removeExtraPaddingFromHeroImage = [
        'zip-with-phone-number-vertical-legacy-compact',
        'eperks',
    ].some((testTemplate) => template === testTemplate);

    // hides the hero image from desktop view
    const isHeroImageHiddenInDesktopView = ['zip-with-phone-number-vertical-legacy-plain'].some(
        (testTemplate) => template === testTemplate
    );

    // hides the hero image from mobile view
    const isHeroImageHiddenInMobileView = [
        'zip-with-pdp-mapd',
        'simplified-mobile',
        'zip-with-phone-number-simplified',
        'zip-with-phone-number-vertical-legacy-plain',
    ].some((testTemplate) => mobiletemplate === testTemplate);

    // hides the hero image from mobile view if on a shorter screen
    const isHeroImageHiddenInShortMobileView = [
        'call-agent',
        'zip-only',
        'zip-with-email',
        'zip-with-phone-number-vertical-legacy-plain',
    ].some((testTemplate) => mobiletemplate === testTemplate);

    // revereses the order of DOM elements in mobile view
    const isReversedOrderInMobileView = ['eperks'].some(
        (testTemplate) => mobiletemplate === testTemplate
    );

    // adds extra top-padding in tablet and desktop views
    const addExtraPaddingTop = ['eperks', 'zip-with-phone-number-simplified'].some(
        (testTemplate) => template === testTemplate
    );

    // adds extra top-padding in mobile view
    const addExtraPaddingTopInMobileView = [].some((testTemplate) => template === testTemplate);

    // adds extra bottom-padding in tablet and desktop views
    const addExtraPaddingBottom = ['zip-with-phone-number-simplified'].some(
        (testTemplate) => template === testTemplate
    );

    // adds extra bottom-padding in mobile view
    const addExtraPaddingBottomInMobileView = ['zip-with-phone-number-simplified'].some(
        (testTemplate) => template === testTemplate
    );

    // replace {{TEMPLATE_VALUES}} in child nodes
    let zipFormHeading = zipformheading.replaceAll(
        '{{STATE_NAME}}',
        stateName ? stateName : 'your state'
    );
    zipFormHeading = zipFormHeading.replaceAll('{{CURRENT_YEAR}}', currentPlanYear);
    const supportedElementsArr: string[] = ['h1', 'h2', 'p', 'span'];
    let isSecondH1 = false;
    const parsedChildren = usepartnerapi ? (
        typeof window !== 'undefined' ? (
            <DefaultContent
                pageConfigs={partnerApiConfigs}
                template={template}
                partnerDisplayName={partnerDisplayName}
            />
        ) : (
            <></>
        )
    ) : children ? (
        (children as Array<SimpleChildElementModel>).map((childNode) => {
            // delete second h1 unless needed for excluded county
            // TODO: create a new "swappable-content h1" block instead of just using two h1 blocks
            const hasFallbackH1 =
                enablebenefitpromotion &&
                (children as Array<SimpleChildElementModel>).filter((child) => child.type === 'h1')
                    .length > 1;
            if (childNode.type === 'h1' && hasFallbackH1) {
                if (!isSecondH1) {
                    isSecondH1 = true;
                    if (isExcludedCounty) {
                        // show the fallback (second) h1 only
                        return null;
                    }
                } else {
                    if (!isExcludedCounty) {
                        // show the benefit promo (first) h1 only
                        return null;
                    }
                }
            }
            if (supportedElementsArr.includes(childNode.type)) {
                if (typeof childNode.props.children === 'string') {
                    // string nodes
                    // TODO: POC for now with very primitive logic
                    const modifiedClassName: string = isExcludedCounty
                        ? childNode.props.className
                            .replace('fips-excl hidden', 'fips-excl')
                            .replace('fips-incl', 'fips-incl hidden')
                        : childNode.props.className;
                    return {
                        ...childNode,
                        props: {
                            children: childNode.props.children
                                .replaceAll('{{STATE_NAME}}', stateName ? stateName : 'your state')
                                .replaceAll('{{CURRENT_YEAR}}', currentPlanYear),
                            className: modifiedClassName,
                            id: childNode.props.id,
                        },
                    } as JSX.Element;
                }
                // object nodes
                if (Array.isArray(childNode.props.children)) {
                    const newChildNode = childNode.props.children.map((grandChildNode) => {
                        if (!grandChildNode) return;
                        if (typeof grandChildNode === 'string') {
                            return grandChildNode
                                .replaceAll('{{STATE_NAME}}', stateName ? stateName : 'your state')
                                .replaceAll('{{CURRENT_YEAR}}', currentPlanYear);
                        }
                        const isHighlight = grandChildNode.type === 'mark';
                        if (isHighlight) {
                            const { className } = grandChildNode.props;
                            let newClassName = className;
                            if (className.includes('has-primary-light-color')) {
                                newClassName = 'text-icon-primary';
                            } else if (className.includes('has-primary-color')) {
                                newClassName = 'text-primary';
                            } else if (className.includes('has-primary-dark-color')) {
                                newClassName = 'text-link-visited';
                            } else if (className.includes('has-secondary-color')) {
                                newClassName = 'text-accent';
                            } else if (className.includes('has-tertiary-color')) {
                                newClassName = 'text-icon-alert';
                            }
                            return {
                                ...grandChildNode,
                                type: 'span',
                                props: {
                                    ...grandChildNode.props,
                                    className: newClassName,
                                },
                            };
                        }
                        return grandChildNode;
                    });
                    // TODO: POC for now with very primitive logic
                    const modifiedClassName: string = isExcludedCounty
                        ? childNode.props.className
                            .replace('fips-excl hidden', 'fips-excl')
                            .replace('fips-incl', 'fips-incl hidden')
                        : childNode.props.className;
                    return {
                        ...childNode,
                        props: {
                            children: newChildNode,
                            className: modifiedClassName,
                            id: childNode.props.id,
                        },
                    } as JSX.Element;
                }
            }
            return childNode as JSX.Element;
        })
    ) : null;

    const toCssString = (templateName: string) => {
        return templateName.replace(/-/g, '');
    };

    if (
        status === 'loading' ||
        status === 'pending' ||
        (usepartnerapi && typeof window === 'undefined')
    ) {
        return (
            <div className="fixed inset-0 z-10 bg-white flex items-center justify-center">
                <Spinner size="large" color="primary" />
            </div>
        );
    }

    return (
        <article
            className={classNames(
                styles['heroPaidSearchArticle' + toCssString(template)],
                styles.heroPaidSearchArticle,
                classes,
                backgroundsurface,
                'relative md:pt-0',
                !isLegacyStylingTemplateMobile && !isPerformancePartnersTemplate && 'pt-6',
                !isPerformancePartnersTemplate && 'lg:px-8 xl:px-0',
                isExcludedCounty && 'is-excluded-county'
            )}
            data-qa="landing-page-hero"
            data-template={`${template}_${mobiletemplate}`}
            data-type={template}
            data-employer={`${isEmployer}`}
            data-statespage={`${isStatesPage}`}
            id="heroPaidSearchMainArticle"
        >
            {/* ---- PAID SEARCH TEMPLATES ---- */}
            {isPaidSearchTemplate && (
                <Grid
                    className={classNames(
                        'md:items-center',
                        isVerticalLayoutTemplate && 'md:gap-y-0',
                        addExtraPaddingTop && 'md:pt-6',
                        addExtraPaddingTopInMobileView && 'pt-3',
                        addExtraPaddingBottom && 'md:pb-6 lg:pb-10',
                        addExtraPaddingBottomInMobileView && 'pb-3'
                    )}
                >
                    {/* -- `Legacy` styled template Hero-Paid-Search Image (non-mobile view only) -- */}
                    {isLegacyStylingTemplate && (
                        <>
                            {/* `Default` layout */}
                            {!isVerticalLayoutTemplate && !isHeroImageHiddenInDesktopView && (
                                <div
                                    className={classNames(
                                        styles[
                                        'heroPaidSearchHeroImageContainerLegacyDefault' +
                                        toCssString(template)
                                        ],
                                        'hidden md:flex md:col-span-3 lg:col-span-5 md:justify-center md:items-end',
                                        isExcludedCounty && 'is-excluded-county'
                                    )}
                                    id="heroPaidSearchHeroImageContainerLegacyDefault"
                                >
                                    <HeroImage
                                        heroimagemobile={mobileImage}
                                        heroimagetablet={tabletImage}
                                        heroimagedesktop={desktopImage}
                                    />
                                </div>
                            )}

                            {/* `Vertical` layout */}
                            {isVerticalLayoutTemplate && (
                                <>
                                    <div
                                        className={classNames(
                                            styles[
                                            'heroPaidSearchChildrenWrapperLegacyVertical' +
                                            toCssString(template)
                                            ],
                                            'hidden',
                                            'xl:w-[600px]',
                                            'lg:w-[462px]',
                                            'md:flex',
                                            !evenhorizontallayoutsplittablet &&
                                            (isHeroImageHiddenInDesktopView
                                                ? 'md:col-span-8'
                                                : 'md:col-span-5'),
                                            evenhorizontallayoutsplittablet &&
                                            (isHeroImageHiddenInDesktopView
                                                ? 'md:col-span-8'
                                                : isCompactTemplateInTabletView
                                                    ? 'md:col-span-5'
                                                    : 'md:col-span-4'),
                                            'md:items-center',
                                            !isHeroImageHiddenInDesktopView && 'lg:pl-6',
                                            !evenhorizontallayoutsplit &&
                                            (isHeroImageHiddenInDesktopView
                                                ? 'lg:col-span-12'
                                                : 'lg:col-span-7'),
                                            evenhorizontallayoutsplit &&
                                            (isHeroImageHiddenInDesktopView
                                                ? 'lg:col-span-12'
                                                : isCompactTemplateInDesktopView
                                                    ? 'lg:col-span-8'
                                                    : 'lg:col-span-6'),
                                            isExcludedCounty && 'is-excluded-county'
                                        )}
                                        id="heroPaidSearchChildrenWrapperLegacyVertical"
                                    >
                                        <div
                                            className={classNames(
                                                styles[
                                                'heroPaidSearchChildContentVertical' +
                                                toCssString(template)
                                                ],
                                                styles.heroPaidSearchChildContentVertical,
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroChildrenContainerLegacyVertical"
                                            data-employer={`${isEmployer}`}
                                            data-statespage={`${isStatesPage}`}
                                        >
                                            {parsedChildren}
                                            {template === 'call-agent' && (
                                                <SpeakWithAgent
                                                    headingCopy=""
                                                    fromComponentName={`Hero Paid Seach: ${templateName}`}
                                                    btnStyle="primary"
                                                    textAlign="left"
                                                    tty={false}
                                                    hero
                                                />
                                            )}
                                            {/* `ZIP Only` themed hero content */}
                                            {template === 'zip-only' && (
                                                <ZipOnly
                                                    inputLabel={zipInputLabel}
                                                    buttonTitle={zipButtonTitle}
                                                    zipFormQaValue="legacy"
                                                    moreInfo={isStatesPage}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {!isHeroImageHiddenInDesktopView && (
                                        <div
                                            className={classNames(
                                                styles[
                                                'heroPaidSearchHeroImageContainerLegacyVertical' +
                                                toCssString(template)
                                                ],
                                                'hidden',
                                                'md:flex',
                                                !evenhorizontallayoutsplittablet &&
                                                (isHeroImageHiddenInDesktopView
                                                    ? 'md:col-span-8'
                                                    : 'md:col-span-3'),
                                                evenhorizontallayoutsplittablet &&
                                                (isHeroImageHiddenInDesktopView
                                                    ? 'md:col-span-8'
                                                    : isCompactTemplateInTabletView
                                                        ? 'md:col-span-3'
                                                        : 'md:col-span-3'),
                                                'md:justify-center',
                                                'md:items-end',
                                                removeExtraPaddingFromHeroImage
                                                    ? 'md:pr-4'
                                                    : 'md:pr-0',
                                                'md:pl-4',
                                                'md:pb-0',
                                                !evenhorizontallayoutsplit &&
                                                (removeExtraPaddingFromHeroImage
                                                    ? isHeroImageHiddenInDesktopView
                                                        ? 'lg:col-span-12 lg:pr-4 md:pt-3'
                                                        : 'lg:col-span-5 lg:pr-4 md:pt-3'
                                                    : isHeroImageHiddenInDesktopView
                                                        ? 'lg:col-span-12 lg:pr-16 md:pt-4'
                                                        : 'lg:col-span-5 lg:pr-16 md:pt-4'),
                                                evenhorizontallayoutsplit &&
                                                (removeExtraPaddingFromHeroImage
                                                    ? isHeroImageHiddenInDesktopView
                                                        ? 'lg:col-span-12 lg:pr-4 md:pt-3'
                                                        : isCompactTemplateInDesktopView
                                                            ? 'lg:col-span-4 lg:pr-4 md:pt-3'
                                                            : 'lg:col-span-6 lg:pr-4 md:pt-3'
                                                    : isHeroImageHiddenInDesktopView
                                                        ? 'lg:col-span-12 lg:pr-48 md:pt-8'
                                                        : isCompactTemplateInDesktopView
                                                            ? 'lg:col-span-4 lg:pr-48 md:pt-8'
                                                            : 'lg:col-span-6 lg:pr-0 md:!py-[26px]'),
                                                'lg:pl-0',
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroImageContainerLegacyVertical"
                                        >
                                            <HeroImage
                                                className="h-auto max-w-[159px] md:max-w-[246px] lg:max-w-[430px] w-full"
                                                heroimagemobile={mobileImage}
                                                heroimagetablet={tabletImage}
                                                heroimagedesktop={desktopImage}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* -- `Default` styled template Hero-Paid-Search Image (non-mobile view only) -- */}
                    {!isLegacyStylingTemplate && !isHeroImageHiddenInDesktopView && (
                        <div
                            className={classNames(
                                'hidden md:flex flex-col md:justify-center md:items-end self-end',
                                'col-span-4 md:col-span-3 lg:col-span-5',
                                'pt-8 pb-6 gap-6 lg:pt-0 lg:pb-0',
                                isExcludedCounty && 'is-excluded-county'
                            )}
                            id="heroPaidSearchHeroImageContainerDefault"
                        >
                            {partnerApiConfigs && partnerHeroImg ? (
                                <img src={partnerHeroImg} alt="" />
                            ) : (
                                <HeroImage
                                    heroimagemobile={mobileImage}
                                    heroimagetablet={tabletImage}
                                    heroimagedesktop={desktopImage}
                                />
                            )}
                            <div className="w-full flex md:hidden justify-center gap-8">
                                <div
                                    className="norton-placeholder"
                                    id="DigiCertClickID_onwANETs_4"
                                ></div>
                                <a
                                    href="https://www.bbb.org/us/ca/santa-clara/profile/insurance-companies/ehealthinsurance-services-1216-359700#bbbseal"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <StaticImage
                                        src="../../images/common/BBB.png"
                                        width={58}
                                        height={40}
                                        alt="BBB Accredited Business, BBB rating A plus"
                                        quality={100}
                                    />
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Hero-Paid-Search Content */}

                    {/* -- Mobile View -- */}
                    <div
                        className={classNames(
                            'md:hidden',
                            styles[
                            'heroPaidSearchHeroChildrenWrapperMobile' +
                            toCssString(mobiletemplate)
                            ],
                            styles.heroPaidSearchHeroChildrenWrapperMobile,
                            'col-span-4 my-6',
                            isExcludedCounty && 'is-excluded-county'
                        )}
                        id="heroPaidSearchHeroChildrenWrapperMobile"
                        data-employer={`${isEmployer}`}
                        data-statespage={`${isStatesPage}`}
                    >
                        {/* `Legacy` styled template mobile WP content */}
                        {isLegacyStylingTemplateMobile && (
                            <>
                                {/* Regular order of elements: inner blocks then hero */}
                                {!isReversedOrderInMobileView && (
                                    <>
                                        {/* WP child content */}
                                        <div
                                            className={classNames(
                                                styles[
                                                'heroPaidSearchChildContentLegacyStyle' +
                                                toCssString(mobiletemplate)
                                                ],
                                                styles.heroPaidSearchChildContentLegacyStyle,
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroChildrenContainerMobileLegacy"
                                        >
                                            {parsedChildren}
                                            {mobiletemplate === 'call-agent' && (
                                                <SpeakWithAgent
                                                    headingCopy=""
                                                    fromComponentName={`Hero Paid Seach: ${mobileTemplateName}`}
                                                    btnStyle="primary"
                                                    textAlign="left"
                                                    tty={false}
                                                    hero
                                                />
                                            )}
                                            {/* `ZIP Only` themed hero content */}
                                            {mobiletemplate === 'zip-only' && (
                                                <ZipOnly
                                                    inputLabel={zipInputLabel}
                                                    buttonTitle={zipButtonTitle}
                                                    zipFormQaValue="legacy"
                                                    moreInfo={isStatesPage}
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                'mx-auto',
                                                isVerticalLayoutTemplateMobile &&
                                                'px-12 max-w-screen-xs',
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroImageContainerMobileLegacy"
                                        >
                                            {!isHeroImageHiddenInMobileView && (
                                                <>
                                                    {!isHeroImageHiddenInShortMobileView && (
                                                        <HeroImage
                                                            heroimagemobile={mobileImage}
                                                            heroimagetablet={tabletImage}
                                                            heroimagedesktop={desktopImage}
                                                        />
                                                    )}
                                                    {isHeroImageHiddenInShortMobileView && (
                                                        <HeroImage
                                                            heroimagemobile={mobileImage}
                                                            heroimagetablet={tabletImage}
                                                            heroimagedesktop={desktopImage}
                                                            className="hidden [@media(min-height:475px)]:block"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Reversed order of elements: hero then inner blocks */}
                                {isReversedOrderInMobileView && (
                                    <>
                                        <div
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                'mx-auto',
                                                'mb-4',
                                                isVerticalLayoutTemplateMobile &&
                                                'px-12 max-w-screen-xs',
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroImageContainerMobileLegacyReversed"
                                        >
                                            {!isHeroImageHiddenInMobileView && (
                                                <>
                                                    {!isHeroImageHiddenInShortMobileView && (
                                                        <HeroImage
                                                            heroimagemobile={mobileImage}
                                                            heroimagetablet={tabletImage}
                                                            heroimagedesktop={desktopImage}
                                                        />
                                                    )}
                                                    {isHeroImageHiddenInShortMobileView && (
                                                        <HeroImage
                                                            heroimagemobile={mobileImage}
                                                            heroimagetablet={tabletImage}
                                                            heroimagedesktop={desktopImage}
                                                            className="hidden [@media(min-height:475px)]:block"
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        {/* WP child content */}
                                        <div
                                            className={classNames(
                                                styles[
                                                'heroPaidSearchChildContentLegacyStyle' +
                                                toCssString(mobiletemplate)
                                                ],
                                                styles.heroPaidSearchChildContentLegacyStyle,
                                                isExcludedCounty && 'is-excluded-county'
                                            )}
                                            id="heroPaidSearchHeroChildrenContainerMobileLegacyReversed"
                                        >
                                            {parsedChildren}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        {/* `Default` styled template mobile WP content */}
                        {!isLegacyStylingTemplateMobile && (
                            <div className={styles.heroPaidSearchChildContentDefaultStyle}>
                                <div className="grid grid-cols-12">
                                    <div
                                        className={classNames(
                                            'hidden [@media(min-width:360px)]:col-span-4 [@media(min-width:360px)]:flex justify-end items-end relative',
                                            isExcludedCounty && 'is-excluded-county'
                                        )}
                                        id="heroPaidSearchHeroImageContainerMobileDefault"
                                    >
                                        <HeroImage
                                            className="absolute top-0"
                                            heroimagemobile={mobileImage}
                                            heroimagetablet={tabletImage}
                                            heroimagedesktop={desktopImage}
                                        />
                                    </div>
                                    <div
                                        className={classNames(
                                            'col-span-12 [@media(min-width:360px)]:col-span-8 flex flex-col justify-start',
                                            styles[
                                            'heroPaidSearchChildContentDefaultStyle' +
                                            toCssString(mobiletemplate)
                                            ],
                                            isExcludedCounty && 'is-excluded-county'
                                        )}
                                        id="heroPaidSearchHeroChildrenContainerMobileDefault"
                                    >
                                        {parsedChildren}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* non-WP provided content */}
                        <div>
                            {/* `ZIP with Drug and Doctor` themed hero content */}
                            {mobiletemplate === 'zip-with-drug-and-doctor' && (
                                <ZipWithDrugAndDoctor />
                            )}

                            {/* `ZIP with Stepper` themed hero content */}
                            {mobiletemplate === 'zip-with-stepper' && (
                                <ZipWithStepper pageFilter={pageFilter} />
                            )}

                            {/* `ZIP with Stepper Legacy` themed hero content */}
                            {mobiletemplate === 'zip-with-stepper-legacy' && (
                                <ZipWithStepperLegacy
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                    pageFilter={pageFilter}
                                />
                            )}

                            {/* `ZIP with Stepper Vertical Legacy` themed hero content */}
                            {mobiletemplate === 'zip-with-stepper-vertical-legacy' && (
                                <ZipWithStepperVerticalLegacy
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                    pageFilter={pageFilter}
                                />
                            )}

                            {/* `ZIP with Guided Flow` themed hero content */}
                            {mobiletemplate === 'zip-with-guided-flow' && (
                                <ZipWithGuidedFlow pageFilter={pageFilter} />
                            )}

                            {/* `ZIP with Phone Number` themed hero content */}
                            {mobiletemplate === 'zip-with-phone-number' && (
                                <ZipWithPhoneNumber
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                />
                            )}

                            {/* `ZIP with Phone Number Simplified` themed hero content */}
                            {mobiletemplate === 'zip-with-phone-number-simplified' && (
                                <ZipWithPhoneNumberSimplified
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy` themed hero content */}
                            {mobiletemplate === 'zip-with-phone-number-vertical-legacy' && (
                                <ZipWithPhoneNumberVerticalLegacy
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy Compact` themed hero content */}
                            {mobiletemplate === 'zip-with-phone-number-vertical-legacy-compact' && (
                                <ZipWithPhoneNumberVerticalLegacyCompact
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy Plain` themed hero content */}
                            {mobiletemplate === 'zip-with-phone-number-vertical-legacy-plain' && (
                                <ZipWithPhoneNumberVerticalLegacyPlain
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with PDP and MA CTAs` themed hero content */}
                            {mobiletemplate === 'zip-with-pdp-mapd' && (
                                <ZipWithPdpMapd
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* Simplified Mobile themed hero content */}
                            {mobiletemplate === 'simplified-mobile' && (
                                <SimplifiedMobile
                                    includetrustpilotmobile={includetrustpilotmobile}
                                />
                            )}

                            {/* `Partners` themed hero content */}
                            {mobiletemplate === 'partners' && <Partners />}

                            {/* `Carrier Partner` themed hero content */}
                            {mobiletemplate === 'carrier-partner' && (
                                <CarrierPartner partnerDisplayName={partnerDisplayName} />
                            )}

                            {/* `Call Agent with ZIP` themed hero content */}
                            {mobiletemplate === 'call-agent-with-zip' && (
                                <CallAgentWithZip
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `Welcome Back` themed hero content */}
                            {mobiletemplate === 'welcome-back' && (
                                <WelcomeBack
                                    zipFormQaValue="legacy-mobile"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `Shop` themed hero content (legacy for CMS Review Page) */}
                            {mobiletemplate === 'shop' && <ZipWithDrugAndDoctor />}
                        </div>
                    </div>
                    {/* )} */}

                    {/* -- Desktop View -- */}
                    <div
                        className={classNames(
                            'hidden md:block',
                            styles['heroPaidSearchHeroChildrenWrapper' + toCssString(template)],
                            styles.heroPaidSearchHeroChildrenWrapper,
                            'col-span-4',
                            !isVerticalLayoutTemplate && 'md:col-span-5',
                            isVerticalLayoutTemplate && 'md:col-span-8',
                            !isVerticalLayoutTemplate && 'lg:col-span-7',
                            isVerticalLayoutTemplate && 'lg:col-span-12',
                            !isVerticalLayoutTemplate && 'md:mt-6',
                            !isVerticalLayoutTemplate && 'lg:mt-10',
                            isExcludedCounty && 'is-excluded-county'
                        )}
                        id="heroPaidSearchHeroChildrenWrapper"
                    >
                        {/* `Legacy` styled template desktop WP provided content (if not a vertical layout) */}
                        {isLegacyStylingTemplate && !isVerticalLayoutTemplate && (
                            <div
                                className={classNames(
                                    styles[
                                    'heroPaidSearchChildContentLegacyStyle' +
                                    toCssString(template)
                                    ],
                                    styles.heroPaidSearchChildContentLegacyStyle,
                                    isExcludedCounty && 'is-excluded-county'
                                )}
                                id="heroPaidSearchHeroChildrenContainerLegacy"
                            >
                                {parsedChildren}
                            </div>
                        )}

                        {/* `Default` styled template desktop WP provided content (if not a vertical layout) */}
                        {!isLegacyStylingTemplate && !isVerticalLayoutTemplate && (
                            <div
                                className={classNames(
                                    styles[
                                    'heroPaidSearchChildContentDefaultStyle' +
                                    toCssString(template)
                                    ],
                                    styles.heroPaidSearchChildContentDefaultStyle,
                                    isExcludedCounty && 'is-excluded-county'
                                )}
                                id="heroPaidSearchHeroChildrenContainerDefault"
                            >
                                {parsedChildren}
                            </div>
                        )}

                        {/* non-WP provided content */}
                        <div>
                            {/* `ZIP with Drug and Doctor` themed hero content */}
                            {template === 'zip-with-drug-and-doctor' && <ZipWithDrugAndDoctor />}

                            {/* `ZIP with Stepper` themed hero content */}
                            {template === 'zip-with-stepper' && (
                                <ZipWithStepper pageFilter={pageFilter} />
                            )}

                            {/* `ZIP with Stepper Legacy` themed hero content */}
                            {template === 'zip-with-stepper-legacy' && (
                                <ZipWithStepperLegacy
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                    pageFilter={pageFilter}
                                />
                            )}

                            {/* `ZIP with Stepper Vertical Legacy` themed hero content */}
                            {template === 'zip-with-stepper-vertical-legacy' && (
                                <ZipWithStepperVerticalLegacy
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                    pageFilter={pageFilter}
                                />
                            )}

                            {/* `ZIP with Guided Flow` themed hero content */}
                            {template === 'zip-with-guided-flow' && (
                                <ZipWithGuidedFlow pageFilter={pageFilter} />
                            )}

                            {/* `ZIP with Phone Number` themed hero content */}
                            {template === 'zip-with-phone-number' && (
                                <ZipWithPhoneNumber
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                />
                            )}

                            {/* `ZIP with Phone Number Simplified` themed hero content */}
                            {template === 'zip-with-phone-number-simplified' && (
                                <ZipWithPhoneNumberSimplified
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy` themed hero content */}
                            {template === 'zip-with-phone-number-vertical-legacy' && (
                                <ZipWithPhoneNumberVerticalLegacy
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy Compact` themed hero content */}
                            {template === 'zip-with-phone-number-vertical-legacy-compact' && (
                                <ZipWithPhoneNumberVerticalLegacyCompact
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with Phone Number Vertical Legacy Plain` themed hero content */}
                            {template === 'zip-with-phone-number-vertical-legacy-plain' && (
                                <ZipWithPhoneNumberVerticalLegacyPlain
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `ZIP with PDP and MA CTAs` themed hero content */}
                            {template === 'zip-with-pdp-mapd' && (
                                <ZipWithPdpMapd
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `Partners` themed hero content */}
                            {template === 'partners' && <Partners />}

                            {/* `Carrier Partner` themed hero content */}
                            {template === 'carrier-partner' && (
                                <CarrierPartner partnerDisplayName={partnerDisplayName} />
                            )}

                            {/* `Call Agent with ZIP` themed hero content */}
                            {template === 'call-agent-with-zip' && (
                                <CallAgentWithZip
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `Welcome Back` themed hero content */}
                            {template === 'welcome-back' && (
                                <WelcomeBack
                                    zipFormQaValue="legacy"
                                    stateName={stateName}
                                    planYear={currentPlanYear}
                                    zipFormHeading={zipFormHeading}
                                    zipFormMainCtaCopy={zipformmainctacopy}
                                    zipFormSecondaryCtaCopy={zipformsecondaryctacopy}
                                />
                            )}

                            {/* `Shop` themed hero content (legacy for CMS Review Page) */}
                            {template === 'shop' && <ZipWithDrugAndDoctor />}
                        </div>
                    </div>
                    {/* )} */}
                </Grid>
            )}

            {/* ---- PERFORMANCE PARTNERS TEMPLATES ---- */}
            {isPerformancePartnersTemplate && (
                <>
                    <div className={classNames('block', 'md:hidden')}>
                        {mobiletemplate === 'performance-partners' && (
                            <PerformancePartners
                                stateName={stateName}
                                heroimagemobile={mobileImage}
                                heroimagetablet={tabletImage}
                                heroimagedesktop={desktopImage}
                            >
                                {parsedChildren}
                            </PerformancePartners>
                        )}
                    </div>
                    <div className={classNames('hidden', 'md:block')}>
                        {template === 'performance-partners' && (
                            <PerformancePartners
                                stateName={stateName}
                                heroimagemobile={mobileImage}
                                heroimagetablet={tabletImage}
                                heroimagedesktop={desktopImage}
                            >
                                {parsedChildren}
                            </PerformancePartners>
                        )}
                    </div>
                </>
            )}
        </article>
    );
};

export default HeroPaidSearch;
